/** @format */

import { notificationConstants } from '../modules/notifications/notifications';
import { apiCall } from './apiCall';

export class defaultActions {
    /**
     * @param url
     * @param filter
     * @param orders
     * @param actionType
     * @param callback
     * @param options propagateDataToCallback = false, dispatchAfterCallback = false
     * @param {boolean} [options.propagateDataToCallback=false] Pass response data to callback? [default: false]
     * @param {boolean} [options.dispatchAfterCallback=false] Should the default dispatch be run after custom callback? [default: false]
     * @param {Object} [options.extraParams=null] Params to be passed to the success dispatch [default: null]
     * @returns {function(*): Promise<void>}
     */
    static getCollection = (url, filter, orders, actionType, callback, options = {}) => {
        const { propagateDataToCallback = false, dispatchAfterCallback = false, extraParams = null } = options;
        let onSuccess = callback;
        let onError;

        if (typeof onSuccess === 'function') {
            onError = undefined;
        }

        if (onSuccess !== null && typeof onSuccess === 'object') {
            onSuccess = callback.onSuccess;
            onError = callback.onError;
        }

        return (dispatch) => {
            apiCall
                .get(url, filter, orders)
                .then((collections) => {
                    if (onSuccess) {
                        if (propagateDataToCallback) {
                            onSuccess(collections, extraParams);
                        } else {
                            onSuccess();
                        }
                        if (dispatchAfterCallback === false) {
                            return;
                        }
                    }
                    dispatch(success(collections, extraParams));
                })
                .catch((error) => {
                    if (onError) {
                        if (propagateDataToCallback) {
                            onError(error);
                        } else {
                            onError();
                        }
                    }
                    if (dispatchAfterCallback === false) {
                        return;
                    }
                    dispatch(failure(error));
                });
            return Promise.resolve();
        };

        function success(collections, extraParams) {
            return {
                type: actionType,
                collections: collections,
                extraParams: extraParams,
            };
        }

        function failure(error) {
            return { type: notificationConstants.ERROR };
        }
    };

    /**
     * getCollection ALIAS
     *
     * @param url
     * @param filter
     * @param orders
     * @param actionType
     * @param callback
     * @param options propagateDataToCallback = false, dispatchAfterCallback = false
     * @param {boolean} [options.propagateDataToCallback=false] Pass response data to callback? [default: false]
     * @param {boolean} [options.dispatchAfterCallback=false] Should the default dispatch be run after custom callback? [default: false]
     * @param {Object} [options.extraParams=null] Params to be passed to the success dispatch [default: null]
     * @returns {function(*): Promise<void>}
     */
    static getList = (url, filter, orders, actionType, callback, options = {}) => {
        return defaultActions.getCollection(url, filter, orders, actionType, callback, options);
    };

    /**
     * @param url
     * @param actionType
     * @param callback
     * @param options propagateDataToCallback = false, dispatchAfterCallback = false
     * @param {boolean} [options.propagateDataToCallback=false] Pass response data to callback? [default: false]
     * @param {boolean} [options.dispatchAfterCallback=false] Should the default dispatch be run after custom callback? [default: false]
     * @param {Object} [options.extraParams=null] Params to be passed to the success dispatch [default: null]
     * @returns {Function}
     */
    static getItem = (url, actionType, callback, options = {}) => {
        const { propagateDataToCallback = false, dispatchAfterCallback = false, extraParams = null } = options;

        let onSuccess = callback;
        let onError;

        if (typeof onSuccess === 'function') {
            onError = undefined;
        }

        if (typeof onSuccess === 'object') {
            onSuccess = callback.onSuccess;
            onError = callback.onError;
        }

        return (dispatch) => {
            apiCall
                .get(url)
                .then((item) => {
                    if (onSuccess) {
                        if (propagateDataToCallback) {
                            onSuccess(item, extraParams);
                        } else {
                            onSuccess();
                        }
                        if (dispatchAfterCallback === false) {
                            return;
                        }
                    }
                    dispatch(success(item, actionType, extraParams));
                })
                .catch((error) => {
                    if (onError) {
                        if (propagateDataToCallback) {
                            onError(error);
                        } else {
                            onError();
                        }
                    }
                    if (dispatchAfterCallback === false) {
                        return;
                    }

                    if (error.status === 404) {
                        dispatch(itemNotFound(error));
                    } else {
                        dispatch(failure(error));
                    }
                });
        };

        function success(item, extraParams) {
            return { type: actionType, item: item, extraParams: extraParams };
        }

        function itemNotFound(error) {
            return {
                type: notificationConstants.NOTFOUND,
                error: error,
            };
        }

        function failure(error) {
            return { type: notificationConstants.ERROR };
        }
    };

    static getItemWithFullResponse = (url, actionType, callback, options = {}) => {
        const { propagateDataToCallback = false, dispatchAfterCallback = false, extraParams = null } = options;

        let onSuccess = callback;
        let onError;

        if (typeof onSuccess === 'function') {
            onError = undefined;
        }

        if (typeof onSuccess === 'object') {
            onSuccess = callback.onSuccess;
            onError = callback.onError;
        }

        return (dispatch) => {
            apiCall
                .getWithResponse(url)
                .then((response) => {
                    if (onSuccess) {
                        if (propagateDataToCallback) {
                            onSuccess(response, extraParams);
                        } else {
                            onSuccess();
                        }
                        if (dispatchAfterCallback === false) {
                            return;
                        }
                    }
                    dispatch(success(response, actionType, extraParams));
                })
                .catch((error) => {
                    if (onError) {
                        if (propagateDataToCallback) {
                            onError(error);
                        } else {
                            onError();
                        }
                    }
                    if (dispatchAfterCallback === false) {
                        return;
                    }

                    if (error.status === 404) {
                        dispatch(itemNotFound(error));
                    } else {
                        dispatch(failure(error));
                    }
                });
        };

        function success(item, extraParams) {
            return { type: actionType, item: item, extraParams: extraParams };
        }

        function itemNotFound(error) {
            return {
                type: notificationConstants.NOTFOUND,
                error: error,
            };
        }

        function failure(error) {
            return { type: notificationConstants.ERROR };
        }
    };

    /**
     * getItem ALIAS
     *
     * @param url
     * @param actionType
     * @param callback
     * @param {object} options propagateDataToCallback = false, dispatchAfterCallback = false
     * @param {boolean} [options.propagateDataToCallback=false] Pass response data to callback? [default: false]
     * @param {boolean} [options.dispatchAfterCallback=false] Should the default dispatch be run after custom callback? [default: false]
     * @returns {Function}
     */
    static get = (url, actionType, callback, options = {}) => {
        return defaultActions.getItem(url, actionType, callback, options);
    };

    static multiPostSingleContext = (url, mutliObject, actionType, callback, options = {}) => {
        const { propagateDataToCallback = false, dispatchAfterCallback = false, dispatchOnError = true } = options;
        let onSuccess = callback;
        let onError;
        if (typeof onSuccess === 'function') {
            onError = undefined;
        }
        if (typeof onSuccess === 'object') {
            onSuccess = callback.onSuccess;
            onError = callback.onError;
        }
        if (mutliObject && mutliObject.length > 0) {
            return (dispatch) => {
                const request = [];
                for (let i = 0; i < mutliObject.length; i++) {
                    request.push(apiCall.post(url, mutliObject[i]));
                }
                Promise.allSettled(request)
                    .then((result) => {
                        if (onSuccess) {
                            if (propagateDataToCallback) {
                                onSuccess(result);
                            } else {
                                onSuccess();
                            }
                            if (dispatchAfterCallback === false) {
                                return;
                            }
                        }
                        dispatch(success(result));
                    })
                    .catch((error) => {
                        if (onError) {
                            if (propagateDataToCallback) {
                                onError(error);
                            } else {
                                onError();
                            }
                        }
                        if (!dispatchOnError && !dispatchAfterCallback) {
                            return;
                        }
                        dispatch(failure(error));
                    });
            };
        }
        return (dispatch) => failure({ errorMessage: 'multiObject not defined' });

        function success(data) {
            return { type: actionType, data: data };
        }

        function failure(error) {
            return {
                type: notificationConstants.ERROR,
                error: error,
            };
        }
    };

    /**
     * @param url
     * @param params
     * @param actionType
     * @param callback
     * @param options propagateDataToCallback = false, dispatchAfterCallback = false
     * @param {boolean} [options.propagateDataToCallback=false] Pass response data to callback? [default: false]
     * @param {boolean} [options.dispatchAfterCallback=false] Should the default dispatch be run after custom callback? [default: false]
     * @returns {Function}
     */
    static post = (url, params, actionType, callback, options = {}) => {
        const { propagateDataToCallback = false, dispatchAfterCallback = false, dispatchErrorAfterCallback = true } = options;
        let onSuccess = callback;
        let onError;

        if (typeof onSuccess === 'function') {
            onError = undefined;
        }

        if (typeof onSuccess === 'object') {
            onSuccess = callback.onSuccess;
            onError = callback.onError;
        }

        return (dispatch) => {
            apiCall
                .post(url, params)
                .then((data) => {
                    if (onSuccess) {
                        if (propagateDataToCallback) {
                            onSuccess(data);
                        } else {
                            onSuccess();
                        }
                        if (dispatchAfterCallback === false) {
                            return;
                        }
                    }
                    dispatch(success(data));
                })
                .catch((error) => {
                    if (onError) {
                        if (propagateDataToCallback) {
                            onError(error);
                        } else {
                            onError();
                        }
                    }
                    if (!dispatchErrorAfterCallback && !dispatchAfterCallback) {
                        return;
                    }
                    dispatch(failure(error));
                });
        };

        function success(data) {
            return { type: actionType, data: data };
        }

        function failure(error) {
            return {
                type: notificationConstants.ERROR,
                error: error,
            };
        }
    };

    /**
     * @param url
     * @param params
     * @param actionType
     * @param callback
     * @param options propagateDataToCallback = false, dispatchAfterCallback = false, dispatchErrorAfterCallback = true
     * @param {boolean} [options.propagateDataToCallback=false] Pass response data to callback? [default: false]
     * @param {boolean} [options.dispatchAfterCallback=false] Should the default dispatch be run after custom callback? [default: false]
     * @returns {Function}
     */
    static put = (url, params, actionType, callback, options = {}) => {
        const { propagateDataToCallback = false, dispatchAfterCallback = false, dispatchErrorAfterCallback = true } = options;

        let onSuccess = callback;
        let onError;

        if (typeof onSuccess === 'function') {
            onError = undefined;
        }

        if (typeof onSuccess === 'object') {
            onSuccess = callback.onSuccess;
            onError = callback.onError;
        }

        return (dispatch) => {
            apiCall
                .put(url, params)
                .then((data) => {
                    if (callback) {
                        if (propagateDataToCallback) {
                            onSuccess(data);
                        } else {
                            onSuccess();
                        }
                        if (dispatchAfterCallback === false) {
                            return;
                        }
                    }
                    dispatch(success(data));
                })
                .catch((error) => {
                    if (onError) {
                        if (propagateDataToCallback) {
                            onError(error);
                        } else {
                            onError();
                        }
                    }
                    if (!dispatchErrorAfterCallback && !dispatchAfterCallback) {
                        return;
                    }
                    dispatch(failure(error));
                });
        };

        function success(data) {
            return { type: actionType, data: data };
        }

        function failure(error) {
            return {
                type: notificationConstants.ERROR,
                error: error,
            };
        }
    };

    /**
     * @param url
     * @param actionType
     * @param callback
     * @returns {Function}
     */
    static remove = (url, actionType, callback, options = {}) => {
        const { propagateDataToCallback = false, dispatchAfterCallback = false, dispatchErrorAfterCallback = true } = options;

        let onSuccess = callback;
        let onError;

        if (typeof onSuccess === 'function') {
            onError = undefined;
        }

        if (typeof onSuccess === 'object') {
            onSuccess = callback.onSuccess;
            onError = callback.onError;
        }

        return (dispatch) => {
            apiCall
                .remove(url)
                .then((data) => {
                    if (callback) {
                        if (propagateDataToCallback) {
                            onSuccess(data);
                        } else {
                            onSuccess();
                        }
                        if (dispatchAfterCallback === false) {
                            return;
                        }
                    }
                    dispatch(success(data));
                })
                .catch((error) => {
                    if (onError) {
                        if (propagateDataToCallback) {
                            onError(error);
                        } else {
                            onError();
                        }
                    }
                    if (!dispatchErrorAfterCallback && !dispatchAfterCallback) {
                        return;
                    }
                    dispatch(failure(error));
                });
        };

        function success() {
            return { type: actionType };
        }

        function failure(error) {
            return {
                type: notificationConstants.ERROR,
                error: error,
            };
        }
    };
}
